import { t } from "@lingui/macro";
import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import React, { useMemo, useState, useEffect } from "react";

// Types génériques pour la flexibilité
export type Column<T> = {
  key: keyof T & string;
  /** Identifiant React unique (colonnes calculées dont `key` doit rester une vraie clé de T). */
  id?: string;
  header: string;
  align?: "left" | "center" | "right";
  render?: (item: T) => React.ReactNode;
  className?: string;
};

type DataTableProps<T> = {
  columns: Column<T>[];
  data: T[];
  pageSize?: number;
  className?: string;
  title?: string;
  actionButtons?: React.ReactNode;
  loading?: boolean; // 👈 Ajout de la prop loading
  /** Clic sur une ligne (ouvre le détail — pattern console Stripe/Linear). */
  onRowClick?: (item: T) => void;
  /** Message d'état vide personnalisé (par défaut « Aucune donnée disponible »). */
  emptyState?: React.ReactNode;
  /** Classe additionnelle par ligne (ex. surligner les éléments urgents). */
  rowClassName?: (item: T) => string | undefined;
};

export const DataTable = <T extends object>({
  columns,
  data,
  pageSize = 8,
  className,
  title,
  actionButtons,
  loading = false,
  onRowClick,
  emptyState,
  rowClassName,
}: DataTableProps<T>) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Calcul du nombre total de pages
  const totalPages = useMemo(() => {
    return Math.ceil(data.length / pageSize);
  }, [data, pageSize]);

  // Données paginées
  const currentData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return data.slice(start, end);
  }, [data, currentPage, pageSize]);

  // Réinitialiser la page si les données changent ou si la page courante dépasse le total
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    } else if (totalPages === 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Gestion du chargement : afficher un état de chargement
  if (loading) {
    return (
      <div
        className={cn(
          "overflow-hidden rounded-xl-admin border border-admin-border bg-admin-surface",
          className,
        )}
      >
        {(title || actionButtons) && (
          <div className="flex items-center justify-between border-b border-admin-border p-6">
            {title && <h3 className="text-lg font-bold text-admin-text-primary">{title}</h3>}
            {actionButtons && <div className="flex gap-2">{actionButtons}</div>}
          </div>
        )}
        <div className="p-12 text-center text-admin-text-muted">
          <span className="inline-block animate-pulse">Chargement des données...</span>
        </div>
      </div>
    );
  }

  // Si aucune donnée
  if (data.length === 0) {
    return (
      <div
        className={cn(
          "overflow-hidden rounded-xl-admin border border-admin-border bg-admin-surface",
          className,
        )}
      >
        {(title || actionButtons) && (
          <div className="flex items-center justify-between border-b border-admin-border p-6">
            {title && <h3 className="text-lg font-bold text-admin-text-primary">{title}</h3>}
            {actionButtons && <div className="flex gap-2">{actionButtons}</div>}
          </div>
        )}
        <div className="p-12 text-center text-admin-text-muted">
          {emptyState ?? "Aucune donnée disponible"}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl-admin border border-admin-border bg-admin-surface",
        className,
      )}
    >
      {/* Header */}
      {(title || actionButtons) && (
        <div className="flex items-center justify-between border-b border-admin-border p-6">
          {title && <h3 className="text-lg font-bold text-admin-text-primary">{title}</h3>}
          {actionButtons && <div className="flex gap-2">{actionButtons}</div>}
        </div>
      )}

      {/* Tableau */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-admin-background/50 text-admin-text-muted">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.id ?? col.key}
                  className={cn(
                    "px-6 py-3 text-xs font-medium uppercase tracking-wider",
                    col.align === "center" && "text-center",
                    col.align === "right" && "text-right",
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-admin-border">
            {currentData.map((item, rowIndex) => (
              <tr
                key={rowIndex}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
                className={cn(
                  "transition-colors hover:bg-admin-surface-hover",
                  onRowClick && "cursor-pointer",
                  rowClassName?.(item),
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col.id ?? col.key}
                    className={cn(
                      "px-6 py-4 text-admin-text-secondary",
                      col.align === "center" && "text-center",
                      col.align === "right" && "text-right",
                      col.className,
                    )}
                  >
                    {col.render ? col.render(item) : (item[col.key] as React.ReactNode)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination (affichée uniquement si plus d'une page) */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-admin-border p-4">
          <span className="text-sm text-admin-text-muted">
            Page {currentPage} sur {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="rounded-md border border-admin-border bg-admin-surface-elevated p-2 text-admin-text-secondary transition-colors hover:bg-admin-surface-hover hover:text-admin-text-primary disabled:pointer-events-none disabled:opacity-50"
              aria-label={t`Page précédente`}
            >
              <CaretLeftIcon size={16} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={cn(
                  "rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                  currentPage === page
                    ? "border-admin-primary bg-admin-primary text-admin-primary-foreground"
                    : "border-admin-border bg-admin-surface-elevated text-admin-text-secondary hover:bg-admin-surface-hover",
                )}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="rounded-md border border-admin-border bg-admin-surface-elevated p-2 text-admin-text-secondary transition-colors hover:bg-admin-surface-hover hover:text-admin-text-primary disabled:pointer-events-none disabled:opacity-50"
              aria-label={t`Page suivante`}
            >
              <CaretRightIcon size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
